import { useMemo, useState } from 'react';

import {
  NEW_CASES_ROTATION_GROUP,
  type CaseRotationStatus,
  type CaseClusterType,
  type ActiveCase,
  type NewCase,
  type NewCaseClusterType,
} from '#entities';
import { EClusterData, ORGAN_COLORS, TrialBadgeIconPath } from '#shared';

import { EZLayers, ZLayer } from '../../data-context';
import {
  CHAR_WIDTH_FACTOR,
  expandActiveStatuses,
  measureLabelBox,
} from '../../helpers';

interface SvgClusterCaseLabelProps {
  item: CaseClusterType | NewCaseClusterType;
  position?: { x: number; y: number };
  handleSelectCase?: (id: string) => void;
  isRotationActive?: boolean;
  activeRotationStatus?: CaseRotationStatus;
}

export const SvgClusterCaseLabel = ({
  item,
  position,
  handleSelectCase,
  isRotationActive,
  activeRotationStatus,
}: SvgClusterCaseLabelProps) => {
  const isNewCase = item.type === EClusterData.NewCase;
  const organColor = ORGAN_COLORS[item.data.organ.name];
  const mainColor = isNewCase ? '#FF975D' : organColor;
  const labelGap = 2;
  const trialBadgePadding = 4;
  const hasTrial = Boolean(item.data.trialProgram);

  // Measure label size for collision mechanism.
  const compactLabel = isNewCase
    ? item.data.label.split(/\s+/).join('')
    : item.data.unosDonorId;
  const { fontPx: labelFontSizePx } = measureLabelBox(
    compactLabel,
    labelGap,
    hasTrial,
    trialBadgePadding,
  );

  // For rendering: split new-case label into blocks (no internal spaces)
  const parts = isNewCase
    ? item.data.label.split(/\s+/).filter(Boolean)
    : [item.data.unosDonorId];

  // Match previous “highlight” opacity logic
  const activeSet = useMemo(
    () =>
      expandActiveStatuses(isRotationActive ? activeRotationStatus : undefined),
    [isRotationActive, activeRotationStatus],
  );
  const status =
    (item.data as ActiveCase).status || (item.data as NewCase).oscStatus;
  const highlightCase = useMemo(() => {
    if (!isRotationActive) {
      return false;
    }
    if (isNewCase) {
      return activeRotationStatus === NEW_CASES_ROTATION_GROUP;
    }
    return activeSet.has(status);
  }, [isRotationActive, isNewCase, activeSet, status, activeRotationStatus]);
  const highlightedOpacity = isRotationActive ? (highlightCase ? 1 : 0.2) : 1;

  // Hover effects (bold + stronger shadow), works on touch too
  const [hover, setHover] = useState(false);
  const baseFilterId = `labelShadow-${item.data.id}`;
  const strongFilterId = `labelShadowStrong-${item.data.id}`;

  if (!position) {
    return null;
  }

  const trialXPosition =
    position.x +
    compactLabel.length * labelFontSizePx * (CHAR_WIDTH_FACTOR / 2) +
    trialBadgePadding;
  const trialYPosition = position.y - labelFontSizePx / 2;

  const content = (
    <g pointerEvents="auto">
      <defs>
        {/* Base shadow ~ drop-shadow(0 2px 2px rgba(0,0,0,0.7)) */}
        <filter id={baseFilterId} x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow
            dx="0"
            dy="2"
            stdDeviation="2"
            floodColor="rgba(0,0,0,0.7)"
          />
        </filter>
        {/* hover shadow */}
        <filter
          id={strongFilterId}
          x="-60%"
          y="-60%"
          width="220%"
          height="220%"
        >
          <feDropShadow
            dx="0"
            dy="2.5"
            stdDeviation="3.2"
            floodColor="rgba(0,0,0,0.9)"
          />
          <feDropShadow
            dx="0"
            dy="0"
            stdDeviation="2.2"
            floodColor="rgba(0,0,0,0.8)"
          />
        </filter>
      </defs>

      <text
        x={position.x}
        y={position.y}
        textAnchor="middle"
        dominantBaseline="middle"
        fontFamily='"JetBrains Mono", monospace'
        // keep the zero-slash feature; most modern browsers honor this on SVG text
        style={{
          fontFeatureSettings: '"zero" 1',
          cursor: 'pointer',
          transition: 'opacity 1s ease',
        }}
        fontSize={labelFontSizePx}
        fontWeight={hover ? 600 : 500}
        fill={mainColor}
        opacity={highlightedOpacity}
        filter={`url(#${hover ? strongFilterId : baseFilterId})`}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={() => handleSelectCase?.(item.data.id)}
        onTouchEnd={() => handleSelectCase?.(item.data.id)}
      >
        {parts.map((part, idx) => (
          <tspan key={idx} dx={idx === 0 ? undefined : `${labelGap}`}>
            {part}
          </tspan>
        ))}
      </text>
      {hasTrial && (
        <image
          href={TrialBadgeIconPath}
          x={trialXPosition}
          y={trialYPosition}
          width={labelFontSizePx * 0.8} // Scale to ~80% to match visual height of JetBrains Mono
          height={labelFontSizePx * 0.8} // Scale to ~80% to match visual height of JetBrains Mono
          opacity={highlightedOpacity}
          style={{ cursor: 'pointer' }}
          onClick={() => handleSelectCase?.(item.data.id)}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        />
      )}
    </g>
  );

  // On hover => Highlighted layer, otherwise stay in Foreground
  return hover ? (
    <ZLayer zIndex={EZLayers.Highlighted}>{content}</ZLayer>
  ) : (
    <ZLayer zIndex={EZLayers.Foreground}>{content}</ZLayer>
  );
};
